/**
 * Normalizes mixed API response shapes into one parsed payload value.
 *
 * Why this exists:
 * - Legacy and current endpoints in this app return different shapes.
 * - Some return `response.data` as a JSON string.
 * - Some return `response.data` as an object/array.
 * - Some older paths effectively return payload-like objects directly.
 *
 * Behavior:
 * 1) If `response.data` is a string, attempts `JSON.parse`.
 * 2) If parse fails, returns `fallbackValue`.
 * 3) If `response.data` is already present and non-null, returns it as-is.
 * 4) Otherwise returns `response` if it exists, else `fallbackValue`.
 *
 * Developer guidance:
 * - Use this before reading API fields when payload shape may vary.
 * - Pass a fallback that matches expected downstream usage (for example `{}` or `[]`).
 * - This helper only normalizes payload shape; it does not handle API errors.
 */
export const parseApiResponseData = (response, fallbackValue = {}) => {
	const rawData = response?.data;

	if (typeof rawData === 'string') {
		try {
			return JSON.parse(rawData);
		} catch {
			return fallbackValue;
		}
	}

	if (rawData !== undefined && rawData !== null) {
		return rawData;
	}

	if (response !== undefined && response !== null) {
		return response;
	}

	return fallbackValue;
};
